import {CoreObjectId} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType, getObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {ItemForArchive} from 'archive/ArchiveConstants';
import {DataModificationsHeader} from 'audit/DataModifications';
import {patchJSONPromise} from 'util/Net';
import {postJSON} from 'util/TaskRunner';

export function getItemsForArchive(
  itemIds: Array<string>,
  {shouldDetectShortcuts}: {shouldDetectShortcuts?: boolean}
): JQueryPromise<{itemsForArchive: Array<ItemForArchive>}> {
  return postJSON({
    url: '/1/api/archive:get-items-for-archive',
    data: {itemIds, shouldDetectShortcuts},
  });
}

export interface ArchiveItemsResponse {
  updatedIds: Array<CoreObjectId>;
}

/**
 * Groups ids by the legacy API base they write through so a batch of mixed object types is split into
 * one request per endpoint.
 */
function groupIdsByApiBase(ids: Array<CoreObjectId>): Map<string, Array<CoreObjectId>> {
  const idsByApiBase = new Map<string, Array<CoreObjectId>>();
  for (const id of ids) {
    const apiBase = getApiBaseForObjectType(getObjectType({id}));
    const group = idsByApiBase.get(apiBase);
    if (group != null) {
      group.push(id);
    } else {
      idsByApiBase.set(apiBase, [id]);
    }
  }
  return idsByApiBase;
}

/**
 * Modern Core Object implementation of archive request.
 */
export async function archiveItems(
  ids: Array<CoreObjectId>,
  archiveReason: string,
  dataModsHeader?: DataModificationsHeader
): Promise<ArchiveItemsResponse> {
  const responses: Array<ArchiveItemsResponse> = await Promise.all(
    Array.from(groupIdsByApiBase(ids), ([url, groupIds]) =>
      patchJSONPromise({
        url,
        data: {updates: groupIds.map((id) => ({id, isArchived: true, archiveReason}))},
        extraHeaders: dataModsHeader,
      })
    )
  );
  return {updatedIds: responses.flatMap((response) => response.updatedIds)};
}

export interface UnarchiveItemsResponse {
  updatedIds: Array<CoreObjectId>;
}

/**
 * Modern Core Object implementation of unarchive request.
 */
export async function unarchiveItems(
  ids: Array<CoreObjectId>,
  dataModsHeader?: DataModificationsHeader
): Promise<UnarchiveItemsResponse> {
  const responses: Array<UnarchiveItemsResponse> = await Promise.all(
    Array.from(groupIdsByApiBase(ids), ([url, groupIds]) =>
      patchJSONPromise({
        url,
        data: {updates: groupIds.map((id) => ({id, isArchived: false}))},
        extraHeaders: dataModsHeader,
      })
    )
  );
  return {updatedIds: responses.flatMap((response) => response.updatedIds)};
}
